import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { OrderLine } from 'src/app/models';
import { LineStatus, OrderSourceSystem, OrderType } from 'src/app/models/enums';
import { OrderShipment } from 'src/app/models/order-shipment';
import { PopupTooltipComponent } from 'src/app/modules/insight-popup-tooltip/components/insight-popup-tooltip/popup-tooltip.component';
import { OrderUtilityService } from 'src/app/services';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { TrackingNumberPipe } from 'src/app/shared/pipes/tracking-number/tracking-number.pipe';
import { OrderShipmentDetailsProgressComponent } from '../order-shipment-details-progress/order-shipment-details-progress.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { CopyableDirective } from '../../../../shared/directives/copyable-directive';

@Component({
  selector: 'order-shipment-details-header',
  templateUrl: './order-shipment-details-header.component.html',
  styleUrls: ['./order-shipment-details-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    DatePipe,
    TrackingNumberPipe,
    MatIcon,
    PopupTooltipComponent,
    OrderShipmentDetailsProgressComponent,
    NgClass,
    CdkOverlayOrigin,
    MatTooltipModule,
    CopyableDirective
  ],
  standalone: true
})
export class OrderShipmentDetailsHeaderComponent {
  @Input() orderShipment: OrderShipment;

  public dynamicsSourceSystem = OrderSourceSystem.Dynamics;

  public manufacturingProfileEditorBaseLink: string = '';
  public trackerBaseLink: string = '';
  public contentSchedulerBaseLink: string = '';

  // Injected services
  public readonly orderUtilityService = inject(OrderUtilityService);
  private readonly environmentService = inject(EnvironmentService);

  constructor() {
    this.manufacturingProfileEditorBaseLink = this.environmentService.getManufacturingProfileEditorBaseAddress();
    this.trackerBaseLink = this.environmentService.getTrackerBaseAddress();
    this.contentSchedulerBaseLink = this.environmentService.getContentSchedulerBaseAddress();
  }

  public get customerProfileLink(): string {
    return `${this.manufacturingProfileEditorBaseLink}manufacturing-profile/${this.orderShipment.manufacturingProfileId}`;
  }

  public get manufacturingProfileLink(): string {
    return `${this.manufacturingProfileEditorBaseLink}manufacturing-profile/profile-assignments/${this.orderShipment.arcCustomerId}?highlight-profile=${this.orderShipment.digitalSequenceNumber}`;
  }

  public get shippingAddress(): string {
    const streetAndCity = [this.orderShipment.shippingAddress, this.orderShipment.shippingCity]
      .filter(part => part)
      .map(part => part.trim())
      .join(' ');

    return [streetAndCity, this.orderShipment.shippingState, this.orderShipment.shippingZip]
      .filter(part => part)
      .map(part => part.trim())
      .join(', ');
  }

  public isShipmentPastDue(): boolean {
    if (!this.orderShipment) {
      return false;
    }

    const today = new Date();
    const normalizedShipDate = new Date(this.orderShipment.shipDate);
    return normalizedShipDate < today;
  }

  public isTheOrderInHD(): boolean {
    const hdContentFound: OrderLine = this.orderShipment.lines.find(line => line.isHd === true);
    return hdContentFound != null ? true : false;
  }

  public isOrderComplete(orderShipment: OrderShipment): boolean {
    if (orderShipment.status === LineStatus.Complete) { return true; }
    if (orderShipment.status === LineStatus.Shipped) { return true; }
    if (orderShipment.status === LineStatus.PendingShipped) { return true; }

    return false;
  }

  public getOrderTypeName(): string {
    switch (this.orderShipment.orderType) {
      case OrderType.Cinelink:
        return 'Cinelink';
      case OrderType.StreamingServer:
        return 'Streaming';
      default:
        return 'ADEBS';
    }
  }

  public isAdebsOrder(): boolean {
    return this.orderShipment.orderType === OrderType.Adebs;
  }

  public getOrderTypeClass(): string {
    switch (this.orderShipment.orderType) {
      case OrderType.Cinelink:
        return 'order-cinelink';
      case OrderType.StreamingServer:
        return 'order-streaming';
      default:
        return 'order-adebs';
    }
  }

  public shipmentRequiresSchedule(): boolean {
    return this.orderShipment && (this.orderShipment.isScheduleRequired || this.orderShipment.isScheduleCreatedInDatabase);
  }

  public getCommonLicenseDateRange(): string {
    // all license dates are the same when this function runs, so just grab the first one
    const firstLine = this.orderShipment.lines[0];
    const displayValidFrom = this.formatDate(firstLine.validFrom);
    const displayValidTo = this.formatDate(firstLine.validTo);
    return `${displayValidFrom} - ${displayValidTo}`;
  }

  private formatDate(inputDate: Date | null | string): string {
    if (!inputDate) {
      return 'None'; // valid to dates are sometimes null
    }

    if (typeof inputDate === 'string') {
      inputDate = new Date(inputDate);
    }

    return inputDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  }
}
